import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import axios from 'axios'
import { useToast } from '../hooks/useToast'

interface DashboardStats {
  totalTickets: number
  openTickets: number
  closedTickets: number
  avgResolutionTime: number
  activeServices: number
}

const Dashboard: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { showError, showAuthError } = useToast()
  
  const [stats, setStats] = useState<DashboardStats>({
    totalTickets: 0,
    openTickets: 0,
    closedTickets: 0,
    avgResolutionTime: 0,
    activeServices: 0
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setIsLoading(true)
        setError(null)
        
        // Fetch ticket statistics
        const ticketsResponse = await axios.get('/api/tickets/stats/overview')
        
        // Fetch services count (we'll need to create this endpoint)
        const servicesResponse = await axios.get('/api/services/active/count')
        
        if (ticketsResponse.data.success && servicesResponse.data.success) {
          setStats({
            totalTickets: ticketsResponse.data.data.totalTickets || 0,
            openTickets: ticketsResponse.data.data.openTickets || 0,
            closedTickets: ticketsResponse.data.data.closedTickets || 0,
            avgResolutionTime: ticketsResponse.data.data.avgResolutionTime || 0,
            activeServices: servicesResponse.data.data.count || 0
          })
        } else {
          setError('Failed to fetch dashboard data')
        }
      } catch (error: any) {
        console.error('Error fetching dashboard stats:', error)
        
        // Handle authentication errors specifically
        if (error.response?.status === 401 || error.response?.status === 403) {
          showAuthError('Please log in again to access your dashboard')
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/login')
          }, 2000)
        } else {
          setError(error.response?.data?.error || 'Failed to fetch dashboard data')
          showError('Unable to load dashboard data. Please try refreshing the page.')
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboardStats()
  }, [])

  const formatHours = (hours: number) => {
    if (hours === 0) return '0h'
    if (hours < 1) return '<1h'
    if (hours < 24) return `${Math.round(hours)}h`
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    if (remainingHours === 0) return `${days}d`
    return `${days}d ${Math.round(remainingHours)}h`
  }

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-red-900">Error loading dashboard</h3>
          <p className="mt-2 text-sm text-red-500">{error}</p>
          <button 
            className="mt-4 btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-sm text-gray-700">
            Welcome to the Zanders Group Support Platform
          </p>
        </div>
      </div>
      
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900">Total Tickets</h3>
          <p className="mt-2 text-3xl font-bold text-primary-600">{stats.totalTickets}</p>
          <p className="mt-1 text-sm text-gray-500">All time</p>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900">Active Tickets</h3>
          <p className="mt-2 text-3xl font-bold text-blue-600">{stats.openTickets}</p>
          <p className="mt-1 text-sm text-gray-500">Currently open</p>
        </div>
        
        <div className="card cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => navigate('/services')}>
          <h3 className="text-lg font-medium text-gray-900">Services</h3>
          <p className="mt-2 text-3xl font-bold text-green-600">{stats.activeServices}</p>
          <p className="mt-1 text-sm text-gray-500">
            {user?.roles.includes('client') ? 'Your active subscriptions' : 'Total client subscriptions'}
          </p>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900">Response Time</h3>
          <p className="mt-2 text-3xl font-bold text-purple-600">{formatHours(stats.avgResolutionTime)}</p>
          <p className="mt-1 text-sm text-gray-500">Average resolution</p>
        </div>
      </div>

      {/* Additional insights */}
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900">Ticket Status Overview</h3>
          <div className="mt-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Open Tickets</span>
              <span className="text-sm font-medium text-blue-600">{stats.openTickets}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Closed Tickets</span>
              <span className="text-sm font-medium text-green-600">{stats.closedTickets}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Tickets</span>
              <span className="text-sm font-medium text-gray-900">{stats.totalTickets}</span>
            </div>
          </div>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900">Performance Metrics</h3>
          <div className="mt-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Resolution</span>
              <span className="text-sm font-medium text-purple-600">{formatHours(stats.avgResolutionTime)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {user?.roles.includes('client') ? 'Your Services' : 'Client Services'}
              </span>
              <span className="text-sm font-medium text-green-600">{stats.activeServices}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Success Rate</span>
              <span className="text-sm font-medium text-green-600">
                {stats.totalTickets > 0 ? Math.round((stats.closedTickets / stats.totalTickets) * 100) : 0}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
